import { Drawer } from '@material-ui/core';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { uuid } from 'uuidv4';
import { getDocumentHelper } from '../../../../actions/utility-actions';
import { Button } from '../../../../components/button/Button';
import { FlyoutFooter } from '../../../../components/flyout-components/FlyoutFooter';
import { FlyoutTitle } from '../../../../components/flyout-components/FlyoutTitle';
import { PageHeading } from '../../../../components/page-components/_index';
import { PagedList } from '../../../../components/paged-list/PagedList';
import { PagedApiResult, PageData } from '../../../../models/utilities/paged-request';
import { PriceSummaryTotals } from '../../../../shared/models/price-summary-totals';
import { CreditHoldReason, CreditHoldReasonMetadata } from '../../../../support/models/enums/credit-hold-reason';
import { StatusCode } from '../../../../utils/api';
import { AppUtils } from '../../../../utils/app-utils';
import { AgingReport } from '../../../../modules/ordering/models/credit-hold-aging-report';
import { CreditHoldDetailFilters } from '../../../../modules/ordering/models/credit-hold-detail-filters';
import { CreditHoldOrderDetails } from '../../../../modules/ordering/models/credit-hold-details';
import { DocumentAgingReportExport } from '../../../../modules/ordering/models/document-aging-report';
import { OrderingSvc } from '../../../../modules/ordering/service';
import { CreditHoldAgingReport } from '../../../../modules/ordering/components/CreditHoldAgingReport';
import { CreditHoldTotalBar } from './CreditHoldTotalBar';
import CreditHoldDetailsRow from './CreditHoldDetailsRow';

interface IProps {
    isOpen: boolean;
    onClose: any;
    filters: CreditHoldDetailFilters | undefined;
    dealer: string;
    creditHoldReason: CreditHoldReason | undefined;
}

export const CreditHoldFlyout = (props: IProps) => {

    const dispatch = useDispatch();
    const [creditHoldReasonDisplay, setCreditHoldReasonDisplay] = React.useState("");
    const [selectedTabIdx, setSelectedTabIdx] = React.useState(0);
    const [service] = React.useState(new OrderingSvc());
    const [hasSelected, setHasSelected] = React.useState(false);
    const [agingReport, setAgingReport] = React.useState<AgingReport|undefined>(undefined);
    const [filters, setFilters] = React.useState<CreditHoldDetailFilters | undefined>(undefined);
    const [selectedOrderTotals, setSelectedOrderTotals] = React.useState<PriceSummaryTotals>(new PriceSummaryTotals());
    const [pendingOrders, setPendingOrders] = React.useState<PagedApiResult<CreditHoldOrderDetails>>(() => {
        const initial = new PagedApiResult<CreditHoldOrderDetails>();
        initial.pagination = new PageData(0);
        return initial;
    });

    const TAB_ORDER = 0;
    const TAB_AGING = 1;

    /**
     * Get orders for this dealer
     * @param filter The search filter passed from the parent control
     */
    async function getDealerHolds(f: CreditHoldDetailFilters) {

        const response = await service.search(CreditHoldOrderDetails, f!);
        if (response.status === StatusCode.Success) {
            const cpy = { ...pendingOrders };
            response.data.results.forEach((itm: CreditHoldOrderDetails) => itm.uuid = uuid());
            cpy.results = response.data.results;

            setPendingOrders(cpy);
            setSelectedOrderTotals(new PriceSummaryTotals());
            setHasSelected(false);
        }
    }

    async function getAgingReport(f: CreditHoldDetailFilters) {
        const dId = AppUtils.getQsValue(f, "dealerId");
        if(dId) {
            const response = await service.getAgingReport(dId);
            if(response.status === StatusCode.Success) {
                setAgingReport(response.data);
            }
        }
    }

    /**
     * When the flyout opens, hydrate the view
     */
    React.useEffect(() => {
        if (props.isOpen && props.filters) {
            setFilters({...props.filters});
            getDealerHolds(props.filters);
            getAgingReport(props.filters);
            setSelectedTabIdx(0);

            if (props.creditHoldReason !== undefined) {
                let reason = CreditHoldReasonMetadata[props.creditHoldReason.toString() as keyof typeof CreditHoldReasonMetadata];
                setCreditHoldReasonDisplay(reason.display);
            }
        }
    }, [props.isOpen]);

    /**
     * Handler for when the user is done drag selecting items
     * @param evt The draggable event with selected items
     */
    const onDragComplete = (evt: any) => {

        let o = pendingOrders.results;

        for (let i = 0; i < o.length; i++) {

            let foundEvtMatch = false;
            const orderItem = o[i];

            for (let j = 0; j < evt.length; j++) {
                const evtItem = evt[j];
                if (evtItem.props.orderItem.id === orderItem.id) {
                    foundEvtMatch = true;
                    break;
                }
            }

            o[i].isChecked = (foundEvtMatch) ? true : false;
        }

        // Update the running totals
        const selecteds = o.filter((itm: CreditHoldOrderDetails) => {
            return itm.isChecked;
        });

        const totals = AppUtils.calculatePrices(selecteds);
        setSelectedOrderTotals(totals);
        setHasSelected(selecteds && selecteds.length > 0);
    };

    /**
     * Make a request to approve all the selected orders.  
     * If successful, requery for orders
     */
    const approveOrders = async () => {
        const selecteds = pendingOrders.results
            .filter((itm: CreditHoldOrderDetails) => {
                return itm.isChecked;
            })
            .map((itm: CreditHoldOrderDetails) => itm.id);

        const result = await service.approveCreditHolds(selecteds);
        if(result.status === StatusCode.Success) {
            await getDealerHolds(filters!);
        }
    }

    /**
     * Make a request to export the aging report to Excel  
     */
    const exportAgingReport = async () => {
        const dId = AppUtils.getQsValue(filters!, "dealerId");
        if(dId) {
            const req = new DocumentAgingReportExport(dId);
            dispatch(getDocumentHelper(req));
        }
    }

    /**
     * Clean up our mess when closing
     */
    const closeFlyout = () => {
        setHasSelected(false);
        setSelectedTabIdx(TAB_ORDER);
        setPendingOrders(new PagedApiResult<CreditHoldOrderDetails>());
        props.onClose();
    };

    /**
     * Switch UI tabs between details and history
     */
    const onTabChange = (e: any, val: any) => {
        if(val !== selectedTabIdx) {
            setSelectedTabIdx(val);
            setSelectedOrderTotals(new PriceSummaryTotals());
            setHasSelected(false);

            const cpy = { ...pendingOrders };
            cpy.results.forEach((itm: CreditHoldOrderDetails) => itm.isChecked = false);
            setPendingOrders(cpy);
        }
    };

    return (
        !AppUtils.IsEmpty(props.filters)
            ? <Drawer
                anchor="right"
                onClose={closeFlyout}
                open={props.isOpen}>
                <div className="stop-action-drawer large pending-orders-flyout">

                    <FlyoutTitle
                        onMobileClose={closeFlyout}
                        title="Credit Holds"
                    />

                    <div className="row">
                        <div className="col">
                            <h3 className="pending-order-subtitle">
                                Showing details for {props.dealer}
                            </h3>
                            <h3 className="pending-order-subtitle-italic">
                                {creditHoldReasonDisplay}
                            </h3>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12">

                            <PageHeading
                                title=""
                                useTabs={true}
                                tabLabels={['Orders', 'Aging']}
                                onTabChange={onTabChange}
                                selectedTab={selectedTabIdx}
                            />

                            {
                                selectedTabIdx === TAB_ORDER
                                    ? <div>                              
                                        <PagedList
                                            noResultsMessage="There are no pending orders for this dealer."
                                            searchCallback={() => { return; }}
                                            data={ pendingOrders }
                                            filterProps={ props.filters! }
                                            onFilterUpdate={() => { return; }}
                                            skipScroll={ true }
                                            selectableClass="pending-order-selectable"
                                            hidePager={ true }
                                            hideFilterBar={ true }
                                            reduxDispatch={ dispatch }
                                            enabledDrag={ true }
                                            useClientSelectablePositioning={ true }
                                            onDragComplete={ onDragComplete }
                                            showSelectAll={ true }
                                            rowRenderer={(order: CreditHoldOrderDetails, extraProps: any) =>
                                                <CreditHoldDetailsRow 
                                                    key={`pending-order-${order.uuid}`} 
                                                    orderItem={order}
                                                     {...extraProps}
                                                />
                                            }
                                        />

                                        <CreditHoldTotalBar selectedTotals={ selectedOrderTotals } />
                                    </div>
                                    : null
                            }

                            {
                                selectedTabIdx === TAB_AGING
                                    ? <CreditHoldAgingReport report={ agingReport } />
                                    : null
                            }

                        </div>
                    </div>

                    <FlyoutFooter>
                        <div className="row">
                            <div className="col">
                                {
                                    selectedTabIdx === TAB_ORDER
                                        ? <Button
                                            text="Approve"
                                            classes="attention"
                                            testID="close-approve-pending-orders-flyout"
                                            disable={ !hasSelected }
                                            onBtnClick={ approveOrders } 
                                        />
                                        : null
                                }
                                {
                                    selectedTabIdx === TAB_AGING
                                        ? <Button
                                            text="Export"
                                            classes="attention"
                                            testID="close-export-pending-orders-flyout"
                                            disable={ agingReport && agingReport.invoices.length > 0 ? false : true }
                                            onBtnClick={ exportAgingReport } 
                                        />
                                        : null
                                }
                                <Button
                                    text="Close"
                                    classes="action"
                                    testID="close-pending-orders-flyout"
                                    onBtnClick={ closeFlyout } />
                            </div>
                        </div>
                    </FlyoutFooter>

                </div>
            </Drawer>
            : null
    );
};
